// The meetings of a space or of an organisation, in three groups: open now, upcoming, past
// (COMX-FE-03). One component serves the project's "Réunions" tab, the shared space's tab and the
// calendar's "Réunions" view, so members and partners see the same thing: what the API returns.
import { EmptyState, SkeletonList } from '@batiflow/ui';
import {
  Button,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  __css,
  tokens,
} from '@fluentui/react-components';
import { Video24Regular } from '@fluentui/react-icons';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { MinutesSection } from '@/features/minutes';
import { useOrgMeetings, useMeetingListEvents, useSpaceMeetings, type MeetingSummary } from './api';
import { MeetingCard } from './meeting-parts';

const useStyles = __css({"root":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f13377fj"},"top":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"f122n59","Brf1p80":"f1869bpl","i8kkvl":"f1xcgk68","Belr9w4":"f1wn3y0l"},"intro":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Bh6795r":"fqerorx","Bgto7am":"fuzgk9b","sj55zd":"fkfq4zb","Btd35i7":"fjksvth"},"section":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1wn3y0l"},"heading":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Be2twd7":"fkhj508","Bg96gwp":"f1i3iumi","Bhrd7zp":"fl43uef"},"list":{"dclx09":"ftrb29c","Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"fgdu4nw","mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1wn3y0l"}});

/** What a list needs from a query: enough to be fed by the space or the organisation list. */
interface ListResult {
  isPending: boolean;
  isError: boolean;
  data?: { items: MeetingSummary[] } | undefined;
  refetch: () => unknown;
}

const PAGE = 50;

export interface MeetingSectionsProps {
  live: ListResult;
  scheduled: ListResult;
  ended: ListResult;
  /** Show the space and project of each meeting (lists that mix several spaces). */
  showPlace?: boolean;
  /** The button that starts a meeting; null when the reader may not start one. */
  action?: ReactNode;
  /** A sentence above the list (for a partner: whose space this is). */
  intro?: string | undefined;
}

/** The three groups of a list of meetings, with loading, failure and empty states. */
export function MeetingSections({
  live,
  scheduled,
  ended,
  showPlace = false,
  action,
  intro,
}: MeetingSectionsProps) {
  const { t } = useTranslation('meetings');
  const styles = useStyles();
  const results = [live, scheduled, ended];
  const sections: { key: string; title: string; result: ListResult }[] = [
    { key: 'live', title: t('sections.live'), result: live },
    { key: 'scheduled', title: t('sections.scheduled'), result: scheduled },
    { key: 'ended', title: t('sections.ended'), result: ended },
  ];
  const loading = results.some((result) => result.isPending);
  const failed = results.some((result) => result.isError);
  const everyItem = results.flatMap((result) => result.data?.items ?? []);

  return (
    <div className={styles.root}>
      {intro !== undefined || action ? (
        <div className={styles.top}>
          {intro !== undefined ? <p className={styles.intro}>{intro}</p> : null}
          {action}
        </div>
      ) : null}
      {failed ? (
        <MessageBar intent="error" layout="multiline">
          <MessageBarBody>{t('list.loadFailed')}</MessageBarBody>
          <MessageBarActions>
            <Button
              onClick={() => {
                for (const result of results) void result.refetch();
              }}
            >
              {t('list.retry')}
            </Button>
          </MessageBarActions>
        </MessageBar>
      ) : null}
      {loading ? (
        <SkeletonList count={4} />
      ) : !failed && everyItem.length === 0 ? (
        <EmptyState
          illustration={<Video24Regular />}
          title={t('list.empty.title')}
          description={action ? t('list.empty.withAction') : t('list.empty.readOnly')}
        />
      ) : (
        sections
          .filter((section) => (section.result.data?.items.length ?? 0) > 0)
          .map((section) => (
            <section
              key={section.key}
              className={styles.section}
              aria-labelledby={`meetings-${section.key}`}
            >
              <h2 id={`meetings-${section.key}`} className={styles.heading}>
                {section.title}
              </h2>
              <ul className={styles.list}>
                {section.result.data?.items.map((meeting) => (
                  <li key={meeting.id}>
                    <MeetingCard meeting={meeting} showPlace={showPlace} />
                  </li>
                ))}
              </ul>
            </section>
          ))
      )}
    </div>
  );
}

export interface SpaceMeetingsPanelProps {
  spaceId: string;
  action?: ReactNode;
  intro?: string | undefined;
}

/** The meetings (not the calls) of one space, grouped, and its PVs under them. */
export function SpaceMeetingsPanel({ spaceId, action, intro }: SpaceMeetingsPanelProps) {
  useMeetingListEvents();
  const live = useSpaceMeetings(spaceId, { status: 'live', kind: 'meeting', limit: PAGE });
  const scheduled = useSpaceMeetings(spaceId, {
    status: 'scheduled',
    kind: 'meeting',
    limit: PAGE,
  });
  const ended = useSpaceMeetings(spaceId, { status: 'ended', kind: 'meeting', limit: PAGE });
  return (
    <>
      <MeetingSections
        live={live}
        scheduled={scheduled}
        ended={ended}
        action={action}
        intro={intro}
      />
      <MinutesSection spaceId={spaceId} />
    </>
  );
}

export interface OrgMeetingsPanelProps {
  orgId: string;
}

/** The meetings of the organisation the person may join (the calendar's "Réunions" view). */
export function OrgMeetingsPanel({ orgId }: OrgMeetingsPanelProps) {
  useMeetingListEvents();
  const live = useOrgMeetings(orgId, { status: 'live', kind: 'meeting', limit: PAGE });
  const scheduled = useOrgMeetings(orgId, { status: 'scheduled', kind: 'meeting', limit: PAGE });
  const ended = useOrgMeetings(orgId, { status: 'ended', kind: 'meeting', limit: PAGE });
  return <MeetingSections live={live} scheduled={scheduled} ended={ended} showPlace />;
}


import "/opt/batiflow/app/apps/web/src/features/meetings/meetings-panel.griffel.css";